import { Injectable, computed, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, tap, catchError, of } from "rxjs";
import { map } from "rxjs/operators";
import { API_BASE_URL } from "../../core/base.service";

/** 后端 API 统一响应包装 */
interface ApiResponse<T> {
  statusCode: number;
  items: T;
  msg: string;
}

/** 一键喊话权限状态（后端权威校验结果） */
export interface BroadcastEntitlement {
  enabled: boolean;
}

/** 绑定设备项（教师视角：教室名按绑定各自记录） */
export interface BroadcastDeviceItem {
  id: number;
  deviceId: string | null;
  classroomName: string | null;
  lastHeartbeatAt: string | null;
}

/** 设备绑定码生成结果 */
export interface BindCodeResult {
  code: string;
  expiresIn: number;
}

/** 设备邀请码生成结果（8 位、10 分钟有效、一次性；与绑定码同形但输入位置不同，工单 14） */
export interface InviteCodeResult {
  code: string;
  expiresIn: number;
}

/** 接受邀请结果（工单 14） */
export interface InviteAcceptResult {
  deviceId: number;
  classroomName: string;
}

/** 设备激活结果 */
export interface DeviceActivateResult {
  classroomName: string;
  deviceToken: string;
}

/** 发送结果 */
export interface BroadcastSendResult {
  broadcastId: number;
}

/** 记录中的逐台投递状态 */
export interface BroadcastRecordDeviceState {
  deviceId: number;
  classroomName: string;
  status: string;
  finishedAt: string | null;
}

/** 喊话记录 */
export interface BroadcastRecord {
  id: number;
  content: string;
  teacherName: string | null;
  createdAt: string;
  devices: BroadcastRecordDeviceState[];
  /**
   * 该记录由哪条定时任务触发（定时喊话，工单 04）：
   * 非 null = 定时触发；null = 立即发送。
   * 老数据/老后端无此字段 → 归一化为 null（不报错、不显示「定时」标签）。
   */
  scheduleId: number | null;
}

/* ---------------- 定时喊话（预约发送） ---------------- */

/**
 * 定时任务状态：
 * - pending   待发送（可取消）
 * - fired     已执行（已生成喊话记录，firedBroadcastId 指向它）
 * - failed    失败（未处理；此时工作台亮红点）
 * - cancelled 已取消（老师主动撤销，留痕不删行）
 * - closed    失败已处理（老师「立即重发」或「忽略」后的终态，红点随之消失）
 *
 * 注意 closed 不是「已读」——它是任务生命周期的终态，不引入任何已读状态。
 */
export type BroadcastScheduleStatus =
  | "pending"
  | "fired"
  | "failed"
  | "cancelled"
  | "closed";

/** 待发送的定时任务 */
export interface BroadcastSchedulePending {
  id: number;
  content: string;
  deviceIds: number[];
  /** 与 deviceIds 同序；已解绑的教室该位为 null（展示为「已移除的教室」） */
  classroomNames: (string | null)[];
  scheduledAt: string;
  createdAt: string;
}

/** 已结束的定时任务 */
export interface BroadcastScheduleFinished {
  id: number;
  content: string;
  /** 创建时的目标设备 id（失败行「立即重发」要用；已解绑的会在发送时被后端忽略） */
  deviceIds: number[];
  /** 与 deviceIds 同序；已解绑的教室该位为 null */
  classroomNames: (string | null)[];
  scheduledAt: string;
  status: Exclude<BroadcastScheduleStatus, "pending">;
  /** status=fired 时指向生成的喊话记录 */
  firedBroadcastId: number | null;
  /** status=failed 时的中文原因（直接展示给老师） */
  failReason: string | null;
  firedAt: string | null;
}

/** 定时任务列表（待发送 ≤20 条升序 + 已结束最近 50 条倒序） */
export interface BroadcastSchedules {
  pending: BroadcastSchedulePending[];
  finished: BroadcastScheduleFinished[];
}

/** 创建定时任务结果 */
export interface ScheduleCreateResult {
  scheduleId: number;
}

@Injectable({ providedIn: "root" })
export class BroadcastService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL, { optional: true }) || "";

  /** 当前喊话权限（内存缓存，不持久化；以服务端校验为准） */
  private readonly _entitlement = signal<BroadcastEntitlement | null>(null);
  readonly entitlement = this._entitlement.asReadonly();

  /**
   * 定时任务列表缓存（内存缓存，不持久化）。
   * 只由 mySchedules() 与 refreshSchedules() 写入；**未开通喊话时不应发起请求**
   * （调用方负责）——避免留下"像已开通"的痕迹。
   */
  private readonly _schedules = signal<BroadcastSchedules | null>(null);
  readonly schedules = this._schedules.asReadonly();

  /**
   * 未处理的失败定时任务数（工作台「一键喊话」卡片红点用）。
   *
   * 语义是「存在**未处理**的失败」而非「有未读」：老师在记录页对失败行执行
   * 「立即重发」或「忽略」后该行迁到 closed，红点自然消失——不需要额外的
   * "知道了"按钮，也不需要任何已读状态。
   *
   * **必须以"当前有喊话权限"为前提**：权限被撤销后记录页是整锁的，老师根本进不去
   * 定时任务 Tab、无从处理那些失败。若红点还亮着，就成了一个永远消不掉的噪音，
   * 而这个红点恰恰是本设计里唯一的可靠兜底——它一旦变成噪音就彻底失效了。
   * 同时这也让"缓存里还留着撤销前的失败行"不会误报。
   */
  readonly failedScheduleCount = computed(() => {
    if (this._entitlement()?.enabled !== true) return 0;
    return (
      this._schedules()?.finished.filter((s) => s.status === "failed").length ??
      0
    );
  });

  /**
   * 查询喊话权限（每请求权威校验；进入喊话页/绑定/发送前调用）
   *
   * ⚠ 走的是 **Lite 端点**（`api/broadcastLite/entitlement`，原生 SQL），它是**裸载荷**：
   * 响应体**本身就是** `{ enabled }`，**没有 `items` 外层**。只换 URL 不改解包，
   * 下面那句 `if (!item) throw` 会**每次调用都抛** —— 喊话页直接打不开。
   */
  checkEntitlement(): Observable<BroadcastEntitlement> {
    return this.http
      .get<BroadcastEntitlement>(`${this.apiBaseUrl}/broadcastLite/entitlement`)
      .pipe(
        map((res) => {
          if (!res || typeof res.enabled !== "boolean") {
            throw new Error("Invalid entitlement response");
          }
          return res;
        }),
        tap((entitlement) => this._entitlement.set(entitlement)),
      );
  }

  /** 刷新喊话权限（静默，不抛异常） */
  refreshEntitlement(): Observable<BroadcastEntitlement | null> {
    return this.checkEntitlement().pipe(catchError(() => of(null)));
  }

  /** 兑换喊话码（开通一键喊话） */
  redeemCode(code: string): Observable<BroadcastEntitlement> {
    const formData = new FormData();
    formData.append("code", code.trim().toUpperCase());

    return this.http
      .post<ApiResponse<BroadcastEntitlement>>(
        `${this.apiBaseUrl}/broadcast/redeem`,
        formData,
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200) {
            throw new Error(res.msg || "兑换失败");
          }
          const item = res.items;
          if (!item || item.enabled !== true) {
            throw new Error(res.msg || "兑换失败");
          }
          return item;
        }),
        tap((entitlement) => this._entitlement.set(entitlement)),
      );
  }

  /** 生成设备绑定码（6 位数字、10 分钟有效；需已开通喊话权限） */
  createBindCode(classroomName: string): Observable<BindCodeResult> {
    const formData = new FormData();
    formData.append("classroomName", classroomName.trim());

    return this.http
      .post<ApiResponse<BindCodeResult>>(
        `${this.apiBaseUrl}/broadcast/bind-code`,
        formData,
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200) {
            throw new Error(res.msg || "生成失败");
          }
          const item = res.items;
          if (!item?.code) {
            throw new Error(res.msg || "生成失败");
          }
          return item;
        }),
      );
  }

  /* ---------------- 设备邀请码（工单 14：一台设备多老师） ---------------- */

  /** 为某台已绑定设备生成邀请码（须已绑定该设备；需已开通喊话权限） */
  createInvite(deviceId: number): Observable<InviteCodeResult> {
    const formData = new FormData();
    formData.append("deviceId", String(deviceId));

    return this.http
      .post<ApiResponse<InviteCodeResult>>(
        `${this.apiBaseUrl}/broadcast/device/invite`,
        formData,
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200) {
            throw new Error(res.msg || "生成失败");
          }
          const item = res.items;
          if (!item?.code) {
            throw new Error(res.msg || "生成失败");
          }
          return item;
        }),
      );
  }

  /** 接受邀请：输入 8 位邀请码即建立**自己**与该设备的绑定 */
  acceptInvite(code: string): Observable<InviteAcceptResult> {
    const formData = new FormData();
    formData.append("code", code.trim().toUpperCase());

    return this.http
      .post<ApiResponse<InviteAcceptResult>>(
        `${this.apiBaseUrl}/broadcast/device/invite/accept`,
        formData,
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200) {
            throw new Error(res.msg || "接受邀请失败");
          }
          const item = res.items;
          if (!item?.classroomName) {
            throw new Error(res.msg || "接受邀请失败");
          }
          return item;
        }),
      );
  }

  /** 我的绑定设备列表（Lite 端点：裸载荷 `{ devices }`，无 `items` 外层） */
  myDevices(): Observable<BroadcastDeviceItem[]> {
    return this.http
      .get<{ devices?: BroadcastDeviceItem[] }>(
        `${this.apiBaseUrl}/broadcastLite/devices`,
      )
      .pipe(map((res) => res?.devices ?? []));
  }

  /** 移除某台设备的绑定（仅自己名下） */
  removeDevice(id: number): Observable<boolean> {
    return this.http
      .delete<ApiResponse<{ removed?: boolean }>>(
        `${this.apiBaseUrl}/broadcast/device/my/${id}`,
      )
      .pipe(map((res) => res?.statusCode === 200));
  }

  /** 设备激活（免登录，绑定码即凭证；deviceId 为壳本地持久化的 uuid） */
  deviceActivate(deviceId: string, code: string): Observable<DeviceActivateResult> {
    const formData = new FormData();
    formData.append("deviceId", deviceId.trim());
    formData.append("code", code.trim());

    return this.http
      .post<ApiResponse<DeviceActivateResult>>(
        `${this.apiBaseUrl}/broadcast/device/activate`,
        formData,
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200) {
            throw new Error(res.msg || "激活失败");
          }
          const item = res.items;
          if (!item?.deviceToken) {
            throw new Error(res.msg || "激活失败");
          }
          return item;
        }),
      );
  }

  /** 设备清除全部绑定（凭 deviceToken 鉴权） */
  deviceClear(deviceId: string, deviceToken: string): Observable<number> {
    const formData = new FormData();
    formData.append("deviceId", deviceId.trim());
    formData.append("deviceToken", deviceToken.trim());

    return this.http
      .post<ApiResponse<{ removed?: number }>>(
        `${this.apiBaseUrl}/broadcast/device/clear`,
        formData,
      )
      .pipe(map((res) => res?.items?.removed ?? 0));
  }

  /** 发送喊话（JSON body；403 未开通 / 429 限流 / 400 校验 → HTTP 错误，msg 中文） */
  sendBroadcast(
    deviceIds: number[],
    content: string,
  ): Observable<BroadcastSendResult> {
    return this.http
      .post<ApiResponse<BroadcastSendResult>>(
        `${this.apiBaseUrl}/broadcast/send`,
        { deviceIds, content },
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200 || !res.items?.broadcastId) {
            throw new Error(res.msg || "发送失败");
          }
          return res.items;
        }),
      );
  }

  /** 我的喊话记录（Lite 端点：裸载荷 `{ records, total, current, pageSize }`） */
  myRecords(): Observable<BroadcastRecord[]> {
    return this.http
      .get<{ records?: BroadcastRecord[] }>(
        `${this.apiBaseUrl}/broadcastLite/records`,
      )
      .pipe(
        map((res) =>
          (res?.records ?? []).map((record) => ({
            ...record,
            // 老后端/老数据没有 scheduleId → 归一化为 null（工单 09：不报错、不显示标签）
            scheduleId: record?.scheduleId ?? null,
          })),
        ),
      );
  }

  /** 重发某条历史（同约束；生成新记录） */
  resendBroadcast(id: number): Observable<BroadcastSendResult> {
    return this.http
      .post<ApiResponse<BroadcastSendResult>>(
        `${this.apiBaseUrl}/broadcast/resend/${id}`,
        {},
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200 || !res.items?.broadcastId) {
            throw new Error(res.msg || "重发失败");
          }
          return res.items;
        }),
      );
  }

  /* ---------------- 定时喊话（预约发送） ---------------- */

  /**
   * 把「信封里的业务状态码」挂到抛出的错误上。
   *
   * 后端所有业务错误（400/403/409/429）都由 `WebApiResultMiddleware` 统一包成
   * `{ StatusCode, Items, Msg }`，且 **HTTP 状态恒为 200**（见
   * `DaemonAPI/Middleware/WebApiResultMiddleware.cs`：`new ObjectResult(...)` 不带状态码）。
   * 因此调用方拿到的 `HttpErrorResponse.status` 永远是 200，
   * **只判 `err.status === 403` 的分支是死的**——要判就得判信封码。
   */
  private businessError(msg: string, statusCode: number): Error {
    const error = new Error(msg) as Error & { statusCode?: number };
    error.statusCode = statusCode;
    return error;
  }

  /** 创建定时任务（预约发送一条一次性喊话）。
   *
   * `scheduledAt` 必须是 **`"yyyy-MM-dd HH:mm"` 墙上时间字符串**（服务器本地时区
   * Asia/Shanghai）。**不要传 Date、不要 `toISOString()`** —— UTC 时间戳会因为老师
   * 手机时区不对而发错时间，且极难复现。
   *
   * 两条产品决策写在这里以免被"顺手纠正"：
   * - 创建**不校验设备在线**（深夜排明天早读时全校设备本就离线，校验在线等于功能不可用）；
   * - 创建**不占用限流额度**（限流只在触发时计，否则排 5 条定时就把当天额度用光了）。
   *
   * HTTP 错误：400 参数/时间非法 · 403 未开通喊话 · 409 待发送已达上限（msg 中文）。
   */
  createSchedule(
    deviceIds: number[],
    content: string,
    scheduledAt: string,
  ): Observable<ScheduleCreateResult> {
    return this.http
      .post<ApiResponse<ScheduleCreateResult>>(
        `${this.apiBaseUrl}/broadcast/schedule`,
        { deviceIds, content, scheduledAt },
      )
      .pipe(
        map((res) => {
          if (res.statusCode !== 200 || !res.items?.scheduleId) {
            // 带上信封码：调用方靠它区分 403（未开通）与 409（超上限）
            throw this.businessError(res.msg || "定时失败", res.statusCode);
          }
          return res.items;
        }),
      );
  }

  /** 我的定时任务列表（待发送 ≤20 升序 + 已结束最近 50 倒序）；结果写入缓存供红点派生 */
  mySchedules(): Observable<BroadcastSchedules> {
    return this.http
      .get<
        ApiResponse<{
          pending?: BroadcastSchedulePending[];
          finished?: BroadcastScheduleFinished[];
        }>
      >(`${this.apiBaseUrl}/broadcast/schedule`)
      .pipe(
        map((res) => ({
          pending: res?.items?.pending ?? [],
          finished: res?.items?.finished ?? [],
        })),
        tap((schedules) => this._schedules.set(schedules)),
      );
  }

  /** 刷新定时任务（静默，不抛异常） */
  refreshSchedules(): Observable<BroadcastSchedules | null> {
    return this.mySchedules().pipe(catchError(() => of(null)));
  }

  /** 取消待发送任务（仅 pending 可取消；**无截止线**，触发前 1 分钟也能取消） */
  cancelSchedule(id: number): Observable<void> {
    return this.http
      .delete<ApiResponse<{ cancelled?: boolean }>>(
        `${this.apiBaseUrl}/broadcast/schedule/${id}`,
      )
      .pipe(
        map((res) => {
          // 必须在这里抛：后端业务错误（409 非 pending / 404 非本人）HTTP 状态恒为 200，
          // 只 map 成 boolean 会让调用方的 next 分支把失败当成功——
          // 老师会看到「已取消该定时喊话」，而那条任务其实还在、到点照样发出去。
          if (res.statusCode !== 200) {
            throw this.businessError(res.msg || "取消失败", res.statusCode);
          }
        }),
      );
  }

  /**
   * 关闭一条失败的定时任务（老师点「忽略」，或「立即重发」成功后调用）。
   * 该行迁到 closed，未处理失败数减一 → 工作台红点消失。
   */
  closeSchedule(id: number): Observable<void> {
    return this.http
      .post<ApiResponse<{ closed?: boolean }>>(
        `${this.apiBaseUrl}/broadcast/schedule/${id}/close`,
        {},
      )
      .pipe(
        map((res) => {
          // 同 cancelSchedule：失败必须抛，否则「已忽略该失败」是假的、红点也不会消失
          if (res.statusCode !== 200) {
            throw this.businessError(res.msg || "操作失败", res.statusCode);
          }
        }),
      );
  }

  /** 清除缓存（登出时调用） */
  clearCache(): void {
    this._entitlement.set(null);
    // 必须一并清空：否则下一个账号会看到上一个账号的失败红点
    // （同 auth.service.ts:24 记录的「账号内存缓存」教训）
    this._schedules.set(null);
  }
}
